const OWNED_MARKER = "data-a11y-scroll-region"

interface ScrollableCandidate {
  selector: string
  label: (el: Element) => string
}

const codeBlockLabel = (el: Element): string => {
  const container = el.closest("[data-language]")
  const lang = container?.getAttribute("data-language")
  return lang ? `Scrollable code block, ${lang}` : "Scrollable code block"
}

const tableLabel = (): string => "Scrollable table"

// `.gatsby-highlight[data-language] > pre` — the `pre` is the element
// Prism's own theme sets `overflow: auto` on.
const CANDIDATES: ScrollableCandidate[] = [
  { selector: ".gatsby-highlight[data-language] > pre", label: codeBlockLabel },
  { selector: ".table-wide", label: tableLabel },
]

const applyOrClear = (el: HTMLElement, label: string): void => {
  // 1px tolerance for sub-pixel rounding — matches the tolerance B3 uses
  // for the same measurement.
  const overflows = el.scrollWidth > el.clientWidth + 1
  const owned = el.getAttribute(OWNED_MARKER) === "true"

  if (overflows) {
    el.setAttribute("tabindex", "0")
    el.setAttribute("role", "region")
    el.setAttribute("aria-label", label)
    el.setAttribute(OWNED_MARKER, "true")
  } else if (owned) {
    el.removeAttribute("tabindex")
    el.removeAttribute("role")
    el.removeAttribute("aria-label")
    el.removeAttribute(OWNED_MARKER)
  }
}

/** Re-measures every candidate and adds/removes the tab stop accordingly. */
export const refreshScrollableRegions = (): void => {
  if (typeof document === "undefined") return
  for (const { selector, label } of CANDIDATES) {
    document.querySelectorAll<HTMLElement>(selector).forEach(el => {
      applyOrClear(el, label(el))
    })
  }
}

const RESIZE_DEBOUNCE_MS = 150

// Install once (e.g. from onClientEntry) — calling twice double-
// registers the resize listener. Returns a teardown function.
export const watchScrollableRegions = (): (() => void) => {
  if (typeof window === "undefined") return () => {}
  let handle: number | null = null
  const onResize = (): void => {
    if (handle !== null) window.clearTimeout(handle)
    handle = window.setTimeout(refreshScrollableRegions, RESIZE_DEBOUNCE_MS)
  }
  window.addEventListener("resize", onResize)
  return () => {
    if (handle !== null) window.clearTimeout(handle)
    window.removeEventListener("resize", onResize)
  }
}
